import { Orcamento } from '@/contracts/schemas';
import { formatCurrency, formatDateBR } from './orcamento-calc';
import { isZapOrcaProActive } from './licenca/pro';

/**
 * Gera o template HTML executivo e profissional de Proposta Comercial para impressão/PDF A4.
 */
export function generateProposalHTML(orcamento: Orcamento): string {
  const { perfilMei, cliente, itens } = orcamento;

  const linhasItens = itens.map((item, index) => {
    const isOdd = index % 2 === 1;
    return `
    <tr style="background-color: ${isOdd ? '#f8fafc' : '#ffffff'}; border-bottom: 1px solid #e2e8f0;">
      <td style="padding: 10px 12px; font-size: 12px; color: #64748b; text-align: center;">${String(index + 1).padStart(2, '0')}</td>
      <td style="padding: 10px 12px;">
        <div style="font-size: 13px; font-weight: 700; color: #0f172a; line-height: 1.35;">${item.descricao}</div>
        <div style="margin-top: 3px;">
          <span style="display: inline-block; font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; padding: 2px 7px; border-radius: 4px; background: ${item.tipo === 'servico' ? '#ecfdf5' : '#eff6ff'}; color: ${item.tipo === 'servico' ? '#047857' : '#1d4ed8'}; border: 1px solid ${item.tipo === 'servico' ? '#a7f3d0' : '#bfdbfe'};">
            ${item.tipo === 'servico' ? 'Mão de Obra / Serviço' : 'Material / Produto'}
          </span>
        </div>
      </td>
      <td style="padding: 10px 12px; font-size: 12px; text-align: center; color: #334155; font-weight: 600;">${item.quantidade} <span style="color: #64748b; font-weight: 400; font-size: 11px;">${item.unidade}</span></td>
      <td style="padding: 10px 12px; font-size: 12px; text-align: right; color: #334155; font-variant-numeric: tabular-nums;">${formatCurrency(item.valorUnitario)}</td>
      <td style="padding: 10px 12px; font-size: 13px; font-weight: 700; text-align: right; color: #0f172a; font-variant-numeric: tabular-nums;">${formatCurrency(item.subtotal)}</td>
    </tr>
  `;
  }).join('');

  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8" />
  <title>Proposta Comercial - ${orcamento.numero} - ${perfilMei.nomeFantasia || perfilMei.nome}</title>
  <style>
    @page {
      size: A4 portrait;
      margin: 10mm 14mm 12mm 14mm;
    }
    * {
      box-sizing: border-box;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      color: #0f172a;
      margin: 0;
      padding: 24px;
      background: #f1f5f9;
      line-height: 1.45;
      font-size: 13px;
    }
    .sheet-wrapper {
      max-width: 820px;
      margin: 0 auto;
      background: #ffffff;
      padding: 36px 40px;
      border-radius: 8px;
      box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
      position: relative;
    }
    .no-print-bar {
      max-width: 820px;
      margin: 0 auto 20px auto;
      background: #141c22;
      color: #f8fafc;
      padding: 12px 20px;
      border-radius: 12px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
    }
    .btn-print-action {
      background: #0f8a7e;
      color: #ffffff;
      border: none;
      padding: 10px 18px;
      border-radius: 8px;
      font-weight: 700;
      font-size: 13px;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      transition: background 0.15s ease;
    }
    .btn-print-action:hover {
      background: #0a6b61;
    }
    .btn-close-action {
      background: transparent;
      color: #94a3b8;
      border: 1px solid rgba(255, 255, 255, 0.2);
      padding: 8px 14px;
      border-radius: 8px;
      font-size: 12px;
      cursor: pointer;
      margin-right: 8px;
    }
    .btn-close-action:hover {
      color: #ffffff;
      border-color: rgba(255, 255, 255, 0.4);
    }

    /* Cabecalho Editorial */
    .header-grid {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      border-bottom: 2px solid #0f8a7e;
      padding-bottom: 20px;
      margin-bottom: 22px;
      gap: 20px;
    }
    .brand-section {
      display: flex;
      align-items: center;
      gap: 16px;
      flex: 1;
    }
    .brand-logo-img {
      max-width: 100px;
      max-height: 70px;
      object-fit: contain;
      border-radius: 8px;
      border: 1px solid #e2e8f0;
      padding: 3px;
      background: #ffffff;
      flex-shrink: 0;
    }
    .brand-title {
      font-size: 20px;
      font-weight: 800;
      color: #0f172a;
      letter-spacing: -0.02em;
      line-height: 1.2;
    }
    .brand-subtitle {
      font-size: 12px;
      color: #0f8a7e;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      margin-top: 2px;
    }
    .brand-info {
      font-size: 11.5px;
      color: #475569;
      margin-top: 4px;
      line-height: 1.35;
    }

    .meta-card {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 10px 14px;
      text-align: right;
      min-width: 200px;
      flex-shrink: 0;
    }
    .meta-badge {
      display: inline-block;
      font-size: 10px;
      font-weight: 800;
      color: #0f8a7e;
      background: #e6f4f1;
      padding: 2px 8px;
      border-radius: 4px;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      margin-bottom: 4px;
    }
    .meta-number {
      font-size: 16px;
      font-weight: 800;
      color: #0f172a;
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      letter-spacing: -0.01em;
    }
    .meta-row {
      font-size: 11.5px;
      color: #475569;
      margin-top: 3px;
    }

    /* Card do Cliente */
    .client-card {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 12px 16px;
      margin-bottom: 18px;
      display: flex;
      justify-content: space-between;
      gap: 20px;
    }
    .client-col {
      flex: 1;
    }
    .section-label {
      font-size: 10px;
      font-weight: 800;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      margin-bottom: 4px;
    }
    .client-name {
      font-size: 14px;
      font-weight: 700;
      color: #0f172a;
    }
    .client-contact {
      font-size: 12px;
      color: #475569;
      margin-top: 2px;
    }

    /* Titulo da Proposta */
    .proposal-title-box {
      background: #f1f5f9;
      border-left: 4px solid #0f8a7e;
      padding: 10px 14px;
      margin-bottom: 18px;
      border-radius: 0 6px 6px 0;
    }
    .proposal-title {
      font-size: 15px;
      font-weight: 800;
      color: #0f172a;
    }

    /* Tabela de Itens */
    .items-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 20px;
    }
    .items-table th {
      background: #0f172a;
      color: #ffffff;
      padding: 9px 12px;
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }
    .items-table th:first-child { border-top-left-radius: 6px; }
    .items-table th:last-child { border-top-right-radius: 6px; }

    /* Bloco Financeiro e Resumo */
    .bottom-layout {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 24px;
      margin-bottom: 22px;
    }
    .terms-box {
      flex: 1;
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 12px 14px;
    }
    .terms-row {
      margin-bottom: 6px;
      font-size: 12px;
      color: #334155;
      line-height: 1.4;
      white-space: pre-line;
    }
    .terms-row:last-child { margin-bottom: 0; }
    .terms-row strong { color: #0f172a; }

    .summary-box {
      width: 280px;
      flex-shrink: 0;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      background: #ffffff;
      padding: 12px 16px;
      box-shadow: 0 1px 3px rgba(0,0,0,0.04);
    }
    .summary-line {
      display: flex;
      justify-content: space-between;
      font-size: 13px;
      color: #475569;
      padding: 4px 0;
      font-variant-numeric: tabular-nums;
    }
    .summary-line.total-line {
      border-top: 2px solid #0f8a7e;
      margin-top: 8px;
      padding-top: 8px;
      font-size: 16px;
      font-weight: 800;
      color: #0f172a;
    }
    .total-val {
      color: #0f8a7e;
    }
    .sinal-highlight {
      background: #f0fdfa;
      border: 1px solid #ccfbf1;
      border-radius: 6px;
      padding: 8px 10px;
      margin-top: 8px;
      font-size: 12px;
      color: #047857;
    }

    /* Pix Box */
    .pix-box {
      border: 1px solid #0f8a7e;
      background: #f0fdfa;
      border-radius: 8px;
      padding: 12px 16px;
      margin-bottom: 24px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
    }
    .pix-info {
      flex: 1;
    }
    .pix-title {
      font-weight: 800;
      font-size: 13px;
      color: #065f46;
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .pix-key-display {
      font-family: ui-monospace, SFMono-Regular, monospace;
      font-size: 13px;
      font-weight: 700;
      color: #0f172a;
      background: #ffffff;
      padding: 4px 8px;
      border-radius: 4px;
      border: 1px dashed #0f8a7e;
      display: inline-block;
      margin-top: 4px;
    }

    /* Assinaturas */
    .signatures-grid {
      display: flex;
      justify-content: space-between;
      gap: 40px;
      margin-top: 36px;
      padding-top: 10px;
      page-break-inside: avoid;
    }
    .sig-col {
      flex: 1;
      text-align: center;
    }
    .sig-bar {
      border-top: 1px solid #94a3b8;
      padding-top: 6px;
      font-size: 12px;
      color: #334155;
    }
    .sig-role {
      font-size: 11px;
      color: #64748b;
    }

    .doc-footer {
      border-top: 1px solid #e2e8f0;
      margin-top: 28px;
      padding-top: 8px;
      display: flex;
      justify-content: space-between;
      font-size: 10.5px;
      color: #94a3b8;
    }

    @media print {
      body {
        padding: 0;
        background: #ffffff;
      }
      .sheet-wrapper {
        padding: 0;
        box-shadow: none;
        max-width: 100%;
      }
      .no-print, .no-print-bar {
        display: none !important;
      }
    }
  </style>
</head>
<body>
  <div class="no-print-bar">
    <div style="display: flex; align-items: center; gap: 12px;">
      <span style="font-size: 18px;">📄</span>
      <div>
        <strong style="font-size: 13px;">Proposta Comercial Formal (${orcamento.numero})</strong>
        <div style="font-size: 11px; color: #94a3b8;">Formatado em padrão A4 para impressão ou salvamento em PDF.</div>
      </div>
    </div>
    <div style="display: flex; align-items: center;">
      <button class="btn-close-action" onclick="window.close()">✕ Fechar Prévia</button>
      <button class="btn-print-action" onclick="window.print()">
        <span>🖨️ Salvar em PDF / Imprimir</span>
      </button>
    </div>
  </div>

  <div class="sheet-wrapper">
    <!-- Header -->
    <div class="header-grid">
      <div class="brand-section">
        ${perfilMei.logoUrl ? `
          <img src="${perfilMei.logoUrl}" alt="Logotipo" class="brand-logo-img" />
        ` : `
          <div style="width: 54px; height: 54px; border-radius: 12px; background: #0f8a7e; color: #ffffff; display: flex; align-items: center; justify-content: center; font-size: 22px; font-weight: 800; flex-shrink: 0;">
            ${perfilMei.nomeFantasia ? perfilMei.nomeFantasia.slice(0, 2).toUpperCase() : '⚡'}
          </div>
        `}
        <div>
          <div class="brand-title">${perfilMei.nomeFantasia || perfilMei.nome}</div>
          <div class="brand-subtitle">${perfilMei.ramoAtividade}</div>
          <div class="brand-info">
            Documento: <strong>${perfilMei.documento}</strong> • WhatsApp: <strong>${perfilMei.telefone}</strong><br/>
            ${perfilMei.cidade} - ${perfilMei.uf} ${perfilMei.email ? `• ${perfilMei.email}` : ''}
          </div>
        </div>
      </div>

      <div class="meta-card">
        <div class="meta-badge">Proposta Comercial</div>
        <div class="meta-number">${orcamento.numero}</div>
        <div class="meta-row">Emissão: <strong>${formatDateBR(orcamento.dataCriacao)}</strong></div>
        <div class="meta-row">Validade: <strong>${formatDateBR(orcamento.dataValidade)}</strong></div>
        <div class="meta-row">Prazo: <strong>${orcamento.prazoEstimado || 'A combinar'}</strong></div>
      </div>
    </div>

    <!-- Dados do Cliente -->
    <div class="client-card">
      <div class="client-col">
        <div class="section-label">Destinatário / Cliente</div>
        <div class="client-name">${cliente.nome}</div>
        <div class="client-contact">WhatsApp / Telefone: <strong>${cliente.telefone}</strong></div>
        ${cliente.email ? `<div class="client-contact">E-mail: ${cliente.email}</div>` : ''}
      </div>
      <div class="client-col" style="text-align: right;">
        <div class="section-label">Local da Obra / Serviço</div>
        <div style="font-size: 13px; font-weight: 600; color: #0f172a;">
          ${cliente.endereco ? cliente.endereco : 'Endereço a confirmar no agendamento'}
        </div>
        <div class="client-contact">${cliente.cidade || perfilMei.cidade} - ${perfilMei.uf}</div>
      </div>
    </div>

    <!-- Titulo / Descricao do Objeto -->
    <div class="proposal-title-box">
      <div class="section-label" style="color: #0f8a7e; margin-bottom: 2px;">Serviço Contratado</div>
      <div class="proposal-title">${orcamento.titulo}</div>
    </div>

    <!-- Tabela de Itens -->
    <table class="items-table">
      <thead>
        <tr>
          <th style="width: 40px; text-align: center;">Item</th>
          <th style="text-align: left;">Descrição dos Serviços & Materiais</th>
          <th style="width: 80px; text-align: center;">Qtd</th>
          <th style="width: 100px; text-align: right;">Valor Unit.</th>
          <th style="width: 110px; text-align: right;">Subtotal</th>
        </tr>
      </thead>
      <tbody>
        ${linhasItens}
      </tbody>
    </table>

    <!-- Bloco de Resumo Financeiro & Condicoes -->
    <div class="bottom-layout">
      <div class="terms-box">
        <div class="section-label" style="margin-bottom: 8px;">Condições Gerais & Garantia</div>
        ${orcamento.condicoesPagamento ? `
          <div class="terms-row"><strong>Forma de Pagamento:</strong> ${orcamento.condicoesPagamento}</div>
        ` : ''}
        ${orcamento.prazoEstimado ? `
          <div class="terms-row"><strong>Previsão de Execução:</strong> ${orcamento.prazoEstimado} após aprovação e sinal.</div>
        ` : ''}
        ${orcamento.observacoes ? `
          <div class="terms-row"><strong>Observações / Garantia:</strong> ${orcamento.observacoes}</div>
        ` : ''}
        <div class="terms-row" style="color: #64748b; font-size: 11px; margin-top: 6px;">
          * Proposta válida até ${formatDateBR(orcamento.dataValidade)}. Valores e prazos sujeitos a renegociação após este prazo.
        </div>
      </div>

      <div class="summary-box">
        <div class="summary-line">
          <span>Subtotal dos itens:</span>
          <span>${formatCurrency(orcamento.valorSubtotal)}</span>
        </div>
        ${orcamento.desconto > 0 ? `
          <div class="summary-line" style="color: #dc2626; font-weight: 600;">
            <span>Desconto concedido:</span>
            <span>- ${formatCurrency(orcamento.desconto)}</span>
          </div>
        ` : ''}
        <div class="summary-line total-line">
          <span>VALOR TOTAL:</span>
          <span class="total-val">${formatCurrency(orcamento.valorTotal)}</span>
        </div>

        ${orcamento.valorSinal > 0 ? `
          <div class="sinal-highlight">
            <div style="display: flex; justify-content: space-between; font-weight: 700;">
              <span>Sinal Pix (${orcamento.percentualSinal}%):</span>
              <span>${formatCurrency(orcamento.valorSinal)}</span>
            </div>
            <div style="display: flex; justify-content: space-between; margin-top: 3px; font-size: 11px; color: #475569;">
              <span>Saldo na conclusão:</span>
              <span style="font-weight: 600;">${formatCurrency(orcamento.valorRestante)}</span>
            </div>
          </div>
        ` : ''}
      </div>
    </div>

    <!-- Bloco Pix -->
    <div class="pix-box">
      <div class="pix-info">
        <div class="pix-title">
          <span>⚡ Chave Pix para Reserva de Data / Sinal</span>
        </div>
        <div style="font-size: 12px; color: #065f46; margin-top: 2px;">
          Chave Pix (${perfilMei.tipoChavePix.toUpperCase()}):
          <span class="pix-key-display">${perfilMei.chavePix}</span>
          <span style="margin-left: 8px;">Titular: <strong>${perfilMei.nome}</strong></span>
        </div>
      </div>
      <div style="text-align: right; font-size: 11px; color: #047857;">
        Valor a transferir: <strong style="font-size: 13px;">${formatCurrency(orcamento.valorSinal > 0 ? orcamento.valorSinal : orcamento.valorTotal)}</strong>
      </div>
    </div>

    <!-- Assinaturas -->
    <div class="signatures-grid">
      <div class="sig-col">
        <div class="sig-bar">
          <strong>${perfilMei.nomeFantasia || perfilMei.nome}</strong><br/>
          <span class="sig-role">Prestador de Serviços • ${perfilMei.documento}</span>
        </div>
      </div>
      <div class="sig-col">
        <div class="sig-bar">
          <strong>${cliente.nome}</strong><br/>
          <span class="sig-role">De Acordo / Aceite da Proposta Comercial</span>
        </div>
      </div>
    </div>

    <div class="doc-footer">
      ${isZapOrcaProActive() ? `
        <span>Proposta Comercial Oficial • ZapOrca Pro (Documento Licenciado)</span>
        <span>Emitido para ${perfilMei.nomeFantasia || perfilMei.nome} • Cópia Autêntica</span>
      ` : `
        <span>Proposta gerada eletronicamente • ZapOrca 2.0</span>
        <span>Ecossistema BRUP Ferramentas Úteis • brup-ferramentas-uteis.com.br</span>
      `}
    </div>
  </div>
</body>
</html>`;
}

/**
 * Dispara o diálogo nativo de impressão do navegador com o layout otimizado.
 */
export function printProposal(orcamento: Orcamento): void {
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    alert('Permita pop-ups para visualizar a impressão da proposta.');
    return;
  }

  printWindow.document.open();
  printWindow.document.write(generateProposalHTML(orcamento));
  printWindow.document.close();

  printWindow.onload = () => {
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };
}

/**
 * Cria e baixa um arquivo HTML standalone da proposta.
 */
export function downloadProposalHTML(orcamento: Orcamento): void {
  const html = generateProposalHTML(orcamento);
  const blob = new Blob([html], { type: 'text/html;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Proposta_${orcamento.numero}.html`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
